// 1. Imports
import { Box, Typography, Card, CardContent, IconButton, Menu, MenuItem, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, Tooltip } from "@mui/material";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import ScheduleIcon from '@mui/icons-material/Schedule';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { When } from "react-if";
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import Loading from "../../loading";
import NoData from "../../no-data";
import { RootState, store } from "../../../store";
import { MessageCenterService } from "../../../services/message-center.service";
import { ComposeSearchService } from "../../../services/compose-search.service";
import { MessageCenterTypes } from "../../../types/message-center.types";
import { describeSchedule } from "../../../constants/messageReminders.constants";
import { useNavigate } from "react-router-dom";
import RepeatIcon from '@mui/icons-material/Repeat';
import { permissionCheck } from "../../../helpers/permissionCheck";
import { customerPermissionFlags } from "../../../constants/systemPermissions.constants";
import { GT_COLORS } from "../../../constants/designTokens.constants";


// 2. Component Def
interface Props {}

export const ScheduledMessagesComponent: React.FC<Props> = ({}) => {
    // 2.1 State and Vars
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
    const navigate = useNavigate();
    const [combinedItems, setCombinedItems] = useState<any[]>([]);
    const [recipientCounts, setRecipientCounts] = useState<Map<string, number>>(new Map());
    const [detailItem, setDetailItem] = useState<any | null>(null);
    const canEdit = permissionCheck(customerPermissionFlags.TEAM_LEADER);
    const isAdmin = permissionCheck(customerPermissionFlags.ADMIN);
    const authUser = store.getState().auth.authUser;
    const canEditItem = (item: any) => canEdit && !!item && (isAdmin || item.webAppUserId === authUser?.id);

    // Redux selectors
    const { loading } = useSelector((state: RootState) => state.messageCenter);

    // 2.2 Effects

    // Load scheduled messages on component mount
    useEffect(() => {
        let messages: any[] = [];
        let reminders: any[] = [];

        // Load scheduled messages
        MessageCenterService.getScheduledMessages(
            1, // page
            100, // perPage
            (isValid, message, result) => {
            if (isValid && result) {
                messages = result.map(msg => ({ ...msg, itemType: 'MESSAGE' }));
                // Combine with existing reminders
                setCombinedItems([...messages, ...reminders].sort((a, b) =>
                    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                ));
            } else {
                console.error('Failed to load scheduled messages:', message);
            }
        });

        // Load automated reminders
        MessageCenterService.getAutomatedReminders(
            1, // page
            100, // perPage
            (isValid, message, result) => {
                if (isValid && result) {
                    reminders = result.map(rem => ({ ...rem, itemType: 'REMINDER' }));
                    // Combine with existing messages
                    const combined = [...messages, ...reminders].sort((a, b) =>
                        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                    );
                    setCombinedItems(combined);

                    // Resolve recipient counts for reminders with criteria
                    reminders.forEach((reminder) => {
                        if (reminder.recipientCriteria) {
                            ComposeSearchService.count(reminder.recipientCriteria)
                                .then((res) => {
                                    setRecipientCounts(prev => new Map(prev).set(reminder.id, res.count));
                                })
                                .catch(() => {
                                    // Fall back to recipientUserIds length
                                    setRecipientCounts(prev => new Map(prev).set(reminder.id, reminder.recipientUserIds?.length || 0));
                                });
                        }
                    });
                } else {
                    console.error('Failed to load automated reminders:', message);
                }
            }
        );
        }, []);

    // 2.3 Functions
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, messageId: string) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedMessage(messageId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedMessage(null);
    };

    const getStatusChipSx = (item: any) => {
        if (item.itemType === 'REMINDER') {
            if (item.isActive) {
                return { backgroundColor: GT_COLORS.greenLight, color: GT_COLORS.green, borderColor: GT_COLORS.greenBorder };
            }
            return { backgroundColor: GT_COLORS.badgeGrayBg, color: GT_COLORS.badgeGrayColor };
        }
        // Scheduled message
        return { backgroundColor: GT_COLORS.amberLight, color: GT_COLORS.amber, borderColor: GT_COLORS.amberBorder };
    };

    const getStatusLabel = (item: any): string => {
        if (item.itemType === 'REMINDER') {
            return item.isActive ? 'Active' : 'Inactive';
        } else {
            return 'Scheduled'
        }
    };

    const getMessageIcon = (item: any) => {
        if (item.itemType === 'REMINDER') {
            return <RepeatIcon sx={{ fontSize: '2.5rem', color: GT_COLORS.primary }} />;
        }
        return <ScheduleIcon sx={{ fontSize: '2.5rem', color: GT_COLORS.textMuted }} />;
    };

    const getDeliveryDate = (message: any): string => {
        if (!message.deliveryDate) return '';
        return dayjs(message.deliveryDate).format('MMM DD, YYYY h:mm A');
    };

    const getRecipientCount = (item: any): number => {
        if (item.itemType === 'REMINDER') {
            // Use resolved count from criteria if available
            if (recipientCounts.has(item.id)) {
                return recipientCounts.get(item.id)!;
            }
            // Fall back to snapshot
            return item.recipientUserIds?.length || 0;
        }
        // For scheduled messages, the snapshot is the resolved count
        return item.recipientUserIds?.length || 0;
    };

    const getChannelsDisplay = (message: any): string => {
        if (!message.channels || message.channels.length === 0) return '';
        return message.channels.map((channel: string) =>
            channel.charAt(0).toUpperCase() + channel.slice(1).toLowerCase()
        ).join(', ');
    };

    const handleDeleteMessage = async () => {
        if (!selectedMessage) return;

        await MessageCenterService.deleteScheduledMessage(
            selectedMessage,
            (isValid, message) => {
                if (isValid) {
                    toast.success('Scheduled message deleted successfully');
                    setCombinedItems(prev => prev.filter(item => !(item.itemType === 'MESSAGE' && item.id === selectedMessage)));
                } else {
                    toast.error(message || 'Failed to delete scheduled message');
                }
                handleMenuClose();
            }
        );
    };

    const handleDeleteAutomatedReminder = async () => {
        if (!selectedMessage) return;

        await MessageCenterService.deleteAutomatedReminder(
            selectedMessage,
            (isValid, message) => {
                if (isValid) {
                    toast.success('Automated reminder deleted successfully');
                    setCombinedItems(prev => prev.filter(item => !(item.itemType === 'REMINDER' && item.id === selectedMessage)));
                } else {
                    toast.error(message || 'Failed to delete automated reminder');
                }
                handleMenuClose();
            }
        );
    };

    const handleToggleReminderActive = async () => {
        if (!selectedMessage) return;

        await MessageCenterService.toggleAutomatedReminderActive(
            selectedMessage,
            (isValid, message, result) => {
                if (isValid && result) {
                    const newStatus = result.isActive ? 'activated' : 'deactivated';
                    toast.success(`Automated reminder ${newStatus} successfully`);
                    setCombinedItems(prev => prev.map(item =>
                        item.itemType === 'REMINDER' && item.id === selectedMessage
                            ? { ...item, isActive: result.isActive }
                            : item
                    ));
                } else {
                    toast.error(message || 'Failed to toggle reminder status');
                }
                handleMenuClose();
            }
        );
    };

    const handleEditMessage = async () => {
        if (!selectedMessage) return;

        // Initialize edit mode and navigate to edit flow
        await MessageCenterService.initializeMessageEditMode(
            selectedMessage,
            (isValid, message) => {
                if (isValid) {
                    // Navigate to edit recipients page
                    navigate(`/messages/${selectedMessage}/edit/recipients`);
                } else {
                    toast.error(message || 'Failed to load message for editing');
                }
                handleMenuClose();
            }
        );
    };

    const handleEditAutomatedReminder = async () => {
        if (!selectedMessage) return;

        // Initialize edit mode and navigate to edit flow for reminder
        await MessageCenterService.initializeAutomatedReminderEditMode(
            selectedMessage,
            (isValid, message) => {
                if (isValid) {
                    navigate(`/messages/automated-reminders/${selectedMessage}/edit/recipients`);
                } else {
                    toast.error(message || 'Failed to load reminder for editing');
                }
                handleMenuClose();
            }
        );
    };

    const handleEditItem = async (item: any) => {
        if (item.itemType === 'REMINDER') {
            await MessageCenterService.initializeAutomatedReminderEditMode(
                item.id,
                (isValid, message) => {
                    if (isValid) {
                        navigate(`/messages/automated-reminders/${item.id}/edit/recipients`);
                    } else {
                        toast.error(message || 'Failed to load reminder for editing');
                    }
                }
            );
        } else {
            await MessageCenterService.initializeMessageEditMode(
                item.id,
                (isValid, message) => {
                    if (isValid) {
                        navigate(`/messages/${item.id}/edit/recipients`);
                    } else {
                        toast.error(message || 'Failed to load message for editing');
                    }
                }
            );
        }
    };

    const getDaysOfWeekDisplay = (days: string[]): string => {
        if (!days || days.length === 0) return '';
        const order = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        const labels: Record<string, string> = { SUN: 'Sun', MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat' };
        return days.sort((a, b) => order.indexOf(a) - order.indexOf(b)).map(d => labels[d] || d).join(', ');
    };

    const getReminderTypeDisplay = (type: string) => {
        switch (type) {
            case 'CHARGING': return 'Charging Reminder';
            case 'STAY_CONNECTED': return 'Stay Connected';
            case 'UPDATE_REMINDER': return 'Update Reminder';
            default: return type;
        }
    };

    // 2.4 JSX Return
    return (
        <Box sx={{ position: "relative" }}>
            {/* Loading Overlay */}
            <When condition={loading.getScheduledMessages}>
                <Box sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                }}>
                    <Loading />
                </Box>
            </When>

            {/* Section Title */}
            <Typography
                sx={{ fontSize: '1.5rem', fontWeight: 600, color: GT_COLORS.textPrimary, mb: 3 }}
            >
                Scheduled & Automated Messages
            </Typography>

            {/* Messages Container */}
            <Card
                elevation={0}
                sx={{
                    backgroundColor: GT_COLORS.pageBg,
                    border: `1px solid ${GT_COLORS.border}`,
                    borderRadius: 2.5,
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <When condition={combinedItems.length > 0}>
                        <Box
                            sx={{
                                display: 'flex',
                                overflowX: 'auto',
                                gap: 3,
                                pb: 2, // Padding for shadows
                                pt: 1, // Top padding for shadows
                                px: 1, // Side padding for shadows
                                mx: -1, // Negative margin to offset side padding
                                '&::-webkit-scrollbar': {
                                    height: 8,
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease',
                                },
                                '&:hover::-webkit-scrollbar': {
                                    opacity: 1,
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'rgba(0,0,0,0.1)',
                                    borderRadius: 4,
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                    borderRadius: 4,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                    },
                                },
                            }}
                        >
                            {combinedItems.map((message) => {
                                const editable = canEditItem(message);
                                return (
                                <Card
                                    key={message.id}
                                    elevation={0}
                                    onClick={() => setDetailItem(message)}
                                    sx={{
                                        minWidth: 250,
                                        maxWidth: 250,
                                        cursor: 'pointer',
                                        position: 'relative',
                                        flexShrink: 0,
                                        border: `1px solid ${GT_COLORS.border}`,
                                        borderRadius: 2.5,
                                        transition: 'all 0.2s ease-in-out',
                                        ...(!editable && { opacity: 0.5 }),
                                        '&:hover': {
                                            borderColor: GT_COLORS.primary,
                                            ...(editable && {
                                                boxShadow: `0 4px 12px rgba(32, 111, 165, 0.12)`,
                                                transform: 'translateY(-1px)',
                                            }),
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 2, pb: '16px !important' }}>
                                        {/* Status Chip and Menu */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Chip
                                                label={getStatusLabel(message)}
                                                size="small"
                                                variant="filled"
                                                sx={{
                                                    ...getStatusChipSx(message),
                                                    fontWeight: 500,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                            {editable ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleMenuOpen(e, message.id)}
                                                    sx={{ mt: -0.5, mr: -0.5, color: GT_COLORS.textFaint }}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <Tooltip title={`Created by ${message.createdByName || 'another user'} — view only`} arrow placement="top">
                                                    <LockOutlinedIcon sx={{ fontSize: '1rem', mt: 0.25, mr: -0.25, color: GT_COLORS.textFaint }} />
                                                </Tooltip>
                                            )}
                                        </Box>

                                        {/* Icon */}
                                        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
                                            {getMessageIcon(message)}
                                        </Box>

                                        {/* Title */}
                                        <Typography
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                color: GT_COLORS.textPrimary,
                                                textAlign: 'center',
                                                mb: 1,
                                            }}
                                        >
                                            {message.title}
                                        </Typography>

                                        {/* Additional Info */}
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: GT_COLORS.textMuted, mb: 0.5 }}>
                                                {getRecipientCount(message)} recipients
                                            </Typography>

                                            <When condition={Boolean(getChannelsDisplay(message))}>
                                                <Typography sx={{ fontSize: '0.75rem', color: GT_COLORS.textMuted, mb: 0.5 }}>
                                                    {getChannelsDisplay(message)}
                                                </Typography>
                                            </When>

                                            {/* Scheduled messages: show delivery date */}
                                            <When condition={message.itemType === 'MESSAGE' && Boolean(getDeliveryDate(message))}>
                                                <Typography sx={{ fontSize: '0.75rem', color: GT_COLORS.textMuted, mb: 0.5 }}>
                                                    {getDeliveryDate(message)}
                                                </Typography>
                                            </When>

                                            {/* Reminders: show reminder type */}
                                            <When condition={message.itemType === 'REMINDER' && Boolean(message.reminderType)}>
                                                <Typography sx={{ fontSize: '0.75rem', color: GT_COLORS.textMuted, mb: 0.5 }}>
                                                    {getReminderTypeDisplay(message.reminderType)}
                                                </Typography>
                                            </When>

                                            {/* Created by */}
                                            <When condition={Boolean(message.createdByName)}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                                                    <PersonIcon sx={{ fontSize: '0.75rem', color: GT_COLORS.textFaint }} />
                                                    <Typography sx={{ fontSize: '0.7rem', color: GT_COLORS.textFaint }}>
                                                        {message.createdByName}
                                                    </Typography>
                                                </Box>
                                            </When>
                                        </Box>
                                    </CardContent>
                                </Card>
                                );
                            })}
                        </Box>
                    </When>

                    <When condition={combinedItems.length === 0 && !loading.getScheduledMessages}>
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title="No Scheduled or Automated Messages"
                            description="You don't have any upcoming or automated messages scheduled."
                        />
                    </When>
                </CardContent>
            </Card>

            {/* Detail Modal */}
            <Dialog
                open={Boolean(detailItem)}
                onClose={() => setDetailItem(null)}
                maxWidth="sm"
                fullWidth
            >
                {detailItem && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {getMessageIcon(detailItem)}
                                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: GT_COLORS.textPrimary }} component="span">
                                    {detailItem.title}
                                </Typography>
                            </Box>
                            <IconButton onClick={() => setDetailItem(null)} size="small" sx={{ color: GT_COLORS.textFaint }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <Divider />
                        <DialogContent sx={{ pt: 3 }}>
                            {/* Status */}
                            <Box sx={{ mb: 2.5 }}>
                                <Chip
                                    label={getStatusLabel(detailItem)}
                                    size="small"
                                    variant="filled"
                                    sx={{
                                        ...getStatusChipSx(detailItem),
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </Box>

                            {/* Message Body */}
                            <Box sx={{ mb: 2.5 }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                    Message
                                </Typography>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: GT_COLORS.textPrimary }}>
                                    {detailItem.body}
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 2.5 }} />

                            {/* Recipients */}
                            <Box sx={{ mb: 2 }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                    Recipients
                                </Typography>
                                <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                    {getRecipientCount(detailItem)} users
                                </Typography>
                            </Box>

                            {/* Channels */}
                            <When condition={Boolean(getChannelsDisplay(detailItem))}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                        Delivery Channels
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                        {getChannelsDisplay(detailItem)}
                                    </Typography>
                                </Box>
                            </When>

                            {/* Scheduled Message: Delivery Date */}
                            <When condition={detailItem.itemType === 'MESSAGE' && Boolean(getDeliveryDate(detailItem))}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                        Scheduled Delivery
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                        {getDeliveryDate(detailItem)}
                                    </Typography>
                                </Box>
                            </When>

                            {/* Scheduled Message: Settings */}
                            <When condition={detailItem.itemType === 'MESSAGE'}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                        Settings
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                        {detailItem.isAnnouncement ? 'Sent as Announcement' : 'Sent from user'}
                                        {detailItem.isCritical ? ' • Critical message' : ''}
                                    </Typography>
                                </Box>
                            </When>

                            {/* Reminder: Type */}
                            <When condition={detailItem.itemType === 'REMINDER' && Boolean(detailItem.reminderType)}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                        Reminder Type
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                        {getReminderTypeDisplay(detailItem.reminderType)}
                                    </Typography>
                                </Box>
                            </When>

                            {/* Reminder: Cadence. For Stay Connected, intervalHours is
                                the inactivity threshold — always meaningful. For
                                Charging/Update with an explicit schedule it's a derived
                                API-compat value, so showing "Every N hours" would
                                contradict the Send Time row; frequency IS the send times. */}
                            <When condition={detailItem.itemType === 'REMINDER'
                                && (detailItem.reminderType === 'STAY_CONNECTED' || !detailItem.sendTimes?.length)}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                        {detailItem.reminderType === 'STAY_CONNECTED' ? 'Inactivity Threshold' : 'Cadence'}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                        {detailItem.reminderType === 'STAY_CONNECTED'
                                            ? `Inactive for ${detailItem.intervalHours}+ hours`
                                            : `Every ${detailItem.intervalHours} hours`}
                                    </Typography>
                                </Box>
                            </When>

                            <When condition={detailItem.itemType === 'REMINDER'}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                        Send Time
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                        {describeSchedule(detailItem.sendTimes, detailItem.timezone, detailItem.reminderType, detailItem.intervalHours, detailItem.timezoneStrategy)}
                                    </Typography>
                                </Box>

                                <When condition={detailItem.daysOfWeek?.length > 0}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                            Days of Week
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                            {getDaysOfWeekDisplay(detailItem.daysOfWeek)}
                                        </Typography>
                                    </Box>
                                </When>
                            </When>

                            <Divider sx={{ mb: 2.5 }} />

                            {/* Created By */}
                            <When condition={Boolean(detailItem.createdByName)}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                        Created By
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                        {detailItem.createdByName}
                                    </Typography>
                                </Box>
                            </When>

                            {/* Created At */}
                            <Box sx={{ mb: 1 }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                    Created
                                </Typography>
                                <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                    {dayjs(detailItem.createdAt).format('MMM DD, YYYY h:mm A')}
                                </Typography>
                            </Box>

                            {/* Updated At (reminders) */}
                            <When condition={detailItem.itemType === 'REMINDER' && Boolean(detailItem.updatedAt)}>
                                <Box sx={{ mb: 1 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                                        Last Updated
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: GT_COLORS.textPrimary }}>
                                        {dayjs(detailItem.updatedAt).format('MMM DD, YYYY h:mm A')}
                                    </Typography>
                                </Box>
                            </When>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                            <Button
                                onClick={() => setDetailItem(null)}
                                sx={{
                                    color: GT_COLORS.textMuted,
                                    borderColor: GT_COLORS.borderInput,
                                    textTransform: 'none',
                                    borderRadius: 1,
                                }}
                                variant="outlined"
                            >
                                Close
                            </Button>
                            {canEditItem(detailItem) && (
                                <Button
                                    variant="contained"
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                        setDetailItem(null);
                                        handleEditItem(detailItem);
                                    }}
                                    sx={{
                                        backgroundColor: GT_COLORS.primary,
                                        textTransform: 'none',
                                        borderRadius: 1,
                                        '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                                    }}
                                >
                                    Edit
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: { borderRadius: 1, border: `1px solid ${GT_COLORS.border}` },
                    },
                }}
            >
                <MenuItem onClick={() => {
                    const item = combinedItems.find(item => item.id === selectedMessage);
                    if (item) setDetailItem(item);
                    handleMenuClose();
                }} sx={{ fontSize: '0.875rem' }}>
                    <VisibilityIcon fontSize="small" sx={{ mr: 1, color: GT_COLORS.textMuted }} />
                    View Details
                </MenuItem>
                {(() => {
                    const item = combinedItems.find(item => item.id === selectedMessage);
                    if (!item || !canEditItem(item)) return null;
                    return (
                        <>
                            <MenuItem onClick={() => {
                                if (item.itemType === 'REMINDER') {
                                    handleEditAutomatedReminder();
                                } else {
                                    handleEditMessage();
                                }
                            }} sx={{ fontSize: '0.875rem' }}>
                                <EditIcon fontSize="small" sx={{ mr: 1, color: GT_COLORS.textMuted }} />
                                Edit
                            </MenuItem>
                            {item.itemType === 'REMINDER' && (
                                <MenuItem onClick={handleToggleReminderActive} sx={{ fontSize: '0.875rem' }}>
                                    {item.isActive
                                        ? <ToggleOffIcon fontSize="small" sx={{ mr: 1, color: GT_COLORS.textMuted }} />
                                        : <ToggleOnIcon fontSize="small" sx={{ mr: 1, color: GT_COLORS.textMuted }} />
                                    }
                                    {item.isActive ? 'Deactivate' : 'Activate'}
                                </MenuItem>
                            )}
                            <MenuItem onClick={() => {
                                if (item.itemType === 'REMINDER') {
                                    handleDeleteAutomatedReminder();
                                } else {
                                    handleDeleteMessage();
                                }
                            }} sx={{ color: GT_COLORS.red, fontSize: '0.875rem' }}>
                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                Delete
                            </MenuItem>
                        </>
                    );
                })()}
            </Menu>
        </Box>
    );
}
